import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Mail, MessageCircle } from "lucide-react";
import { api, fmtDate } from "../../lib/api";
import { Badge } from "../../components/Pill";

const STATUS_TONES = { New: "petal", Contacted: "gold", Closed: "ink" };
const NEXT_STATUS = { New: "Contacted", Contacted: "Closed", Closed: "New" };

export default function EnquiriesTab() {
  const [items, setItems] = useState(null);

  const load = () => api("/admin/enquiries").then(setItems).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);

  const setStatus = async (e) => {
    const status = NEXT_STATUS[e.status || "New"];
    try {
      await api(`/admin/enquiries/${e.id}`, { method: "PATCH", body: { status } });
      setItems(items.map((x) => (x.id === e.id ? { ...x, status } : x)));
      toast.success(`Marked as ${status}`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await api(`/admin/enquiries/${id}`, { method: "DELETE" });
      toast.success("Enquiry deleted");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const respond = (e) => {
    const isEmail = e.contact.includes("@");
    if (isEmail) {
      window.location.href = `mailto:${e.contact}?subject=${encodeURIComponent(`Your enquiry to ${e.vendor_name} via The Brides of India`)}&body=${encodeURIComponent(`Hi ${e.couple_name},\n\nThank you for your enquiry through The Brides of India.\n\n`)}`;
    } else {
      const phone = e.contact.replace(/[^0-9]/g, "");
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(`Hi ${e.couple_name}, thank you for your enquiry to ${e.vendor_name} via The Brides of India.`)}`, "_blank");
    }
  };

  return (
    <div data-testid="enquiries-tab">
      <h2 className="font-display uppercase text-2xl md:text-3xl text-ink">Vendor Enquiries</h2>
      <p className="mt-2 text-sm text-neutral-500">Couples reaching out to artisans through the directory. Tap the status to move a lead along.</p>

      <div className="mt-8 space-y-5">
        {!items ? (
          <p className="py-6 font-elegant italic text-xl text-gold">Loading</p>
        ) : items.length === 0 ? (
          <p className="py-6 text-sm text-neutral-500" data-testid="enquiries-empty">No enquiries yet. They will appear here the moment a couple writes in.</p>
        ) : (
          items.map((e) => (
            <div key={e.id} data-testid={`enquiry-row-${e.id}`} className="bg-white border-2 border-gold/10 rounded-[1.5rem] p-5 md:p-6">
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone="blush">{e.vendor_name}</Badge>
                    <button
                      onClick={() => setStatus(e)}
                      data-testid={`enquiry-status-${e.id}`}
                      title="Tap to change status"
                      className="transition-transform duration-200 hover:scale-105"
                    >
                      <Badge tone={STATUS_TONES[e.status || "New"]}>{e.status || "New"}</Badge>
                    </button>
                    <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-ink/40">{fmtDate(e.created_at)}</span>
                  </div>
                  <p className="mt-3 font-display uppercase text-lg text-ink">{e.couple_name}</p>
                  <p className="text-sm text-gold break-all">{e.contact}</p>
                  {e.wedding_date && <p className="mt-1 text-xs text-neutral-500">Wedding: {e.wedding_date}</p>}
                  {e.message && <p className="mt-3 text-sm text-neutral-600 leading-relaxed">{e.message}</p>}
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <button onClick={() => respond(e)} data-testid={`enquiry-respond-${e.id}`} aria-label="Respond" title="Respond"
                    className="w-10 h-10 border-2 border-ink/10 rounded-full flex items-center justify-center text-ink/50 hover:border-gold hover:text-gold transition-colors duration-300">
                    {e.contact.includes("@") ? <Mail size={15} /> : <MessageCircle size={15} />}
                  </button>
                  <button onClick={() => remove(e.id)} data-testid={`enquiry-delete-${e.id}`} aria-label="Delete"
                    className="w-10 h-10 border-2 border-ink/10 rounded-full flex items-center justify-center text-ink/50 hover:border-red-400 hover:text-red-500 transition-colors duration-300">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
